import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

/**
 * OAuth callback page for Stripe Connect
 * Handles the redirect from Stripe after user authorization
 */
export function StripeOAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  const stripeConnect = useStripeConnect({
    clientId: import.meta.env.VITE_STRIPE_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/settings/integrations/stripe/callback`,
  });

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth errors from Stripe
      if (error) {
        setStatus('error');
        setMessage(errorDescription || `Stripe authorization failed: ${error}`);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        setStatus('error');
        setMessage('Invalid OAuth callback: missing code or state parameter');
        return;
      }

      // Exchange code for access token
      try {
        const integration = await stripeConnect.handleCallback(code, state);

        if (integration) {
          setStatus('success');
          setMessage(
            `Successfully connected to Stripe! Account: ${integration.name} (${
              integration.livemode ? 'Live' : 'Test'
            } mode)`
          );
        } else {
          setStatus('error');
          setMessage('Failed to complete Stripe authorization');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      }
    };

    handleOAuthCallback();
  }, [searchParams, stripeConnect]);

  const handleContinue = () => {
    navigate('/settings/integrations');
  };

  return (
    <div className="container mx-auto max-w-2xl py-12 px-4">
      <Card>
        <CardHeader>
          <CardTitle>Stripe Connect Authorization</CardTitle>
          <CardDescription>Completing your Stripe integration setup</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {status === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center space-y-4">
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground">
                  Connecting to Stripe and verifying your account...
                </p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <div className="flex items-center justify-center py-4">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-900">{message}</AlertDescription>
              </Alert>
              <div className="flex justify-center">
                <Button onClick={handleContinue}>Go to Integrations Settings</Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="flex items-center justify-center py-4">
                <XCircle className="h-16 w-16 text-destructive" />
              </div>
              <Alert variant="destructive">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={handleContinue}>
                  Go to Settings
                </Button>
                <Button onClick={() => window.location.reload()}>Try Again</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
